/**
 * Build a lower-cabinet for a single wall segment.
 *
 * Cabinets and countertops are now built in separate passes — the
 * countertop is laid down later by `buildCountertopRuns.js`, which
 * spans contiguous counter-height runs (cabinet + dishwasher) instead
 * of one countertop per cabinet.
 */

const { KITCHEN_CABINET_DIMENSIONS } = require('./constants');
const { getWallAxes } = require('./wallMath');

/**
 * @param {Object} wall - Room wall with `alignment.bottomLeft/Right`.
 * @param {{start: number, end: number}} segment - Parametric range along
 *   the wall axis (meters from bottomLeft).
 * @param {number} floorY - Floor Y in world meters.
 * @param {Object} [opts]
 * @param {number} [opts.depth] - Override body depth (e.g. a shallow ~12"
 *   storage run squeezed between a wall and a walkway). Defaults to the
 *   standard 24" cabinet depth.
 * @param {number} [opts.height] - Override body height (e.g. a 32" bathroom
 *   vanity run — see cabinet-runs/profiles.js). Defaults to the standard
 *   34.5" kitchen base.
 * @param {string} [opts.label] - Override the display label ("Vanity
 *   Cabinet" on a vanity-profile run).
 * @returns {{ cabinet: Object }} New cabinet with a fresh `fixtureGroupId`.
 */
function buildLowerCabinetForSegment(wall, segment, floorY, opts = {}) {
  const axes = getWallAxes(wall);
  if (!axes) return { cabinet: null };
  const { start, wallDir, wallNormal: normal, wallRotationY: ry } = axes;

  const segmentWidth = segment.end - segment.start;
  const segmentMid = (segment.start + segment.end) / 2;

  const surfacePoint = {
    x: start.x + segmentMid * wallDir.x,
    z: start.z + segmentMid * wallDir.z,
  };

  const cabinetDepth = opts.depth ?? KITCHEN_CABINET_DIMENSIONS.depth;
  const cabinetHeight = opts.height ?? KITCHEN_CABINET_DIMENSIONS.height;

  // Push cabinet center inward by half its depth so the back face sits
  // flush against the wall surface.
  const cabinetCenter = {
    x: surfacePoint.x + (cabinetDepth / 2) * normal.x,
    y: floorY + cabinetHeight / 2,
    z: surfacePoint.z + (cabinetDepth / 2) * normal.z,
  };

  // Content-keyed, no timestamp: an id says what the object IS (its wall and
  // span start), so identical geometry mints identical ids across rebuilds.
  // A `Date.now()` stamp here looked unique but wasn't — two cabinets minted
  // in the same millisecond got IDENTICAL ids and every id-keyed Map
  // downstream silently collapsed them (see cabinetLayout/fromPhaseB.js for
  // the same convention). One object per (kind, wall, start) may exist; a
  // second is a bug this makes loud instead of a timing flake.
  const stamp = `${wall.id}_${Math.round(segment.start * 1000)}`;
  const fixtureGroupId = `kitchenCabinet_${stamp}`;
  const cabinetId = `cabinet_${stamp}`;

  const now = new Date().toISOString();

  const cabinet = {
    id: cabinetId,
    type: 'lower-cabinet',
    label: opts.label || 'Kitchen Cabinet',
    category: { 'lower-cabinet': true },
    center: cabinetCenter,
    dimensions: {
      x: segmentWidth,
      y: cabinetHeight,
      z: cabinetDepth,
    },
    rotation: { x: 0, y: ry, z: 0 },
    parentWallIds: [wall.id],
    fixtureGroupId,
    addedBy: 'kitchen-layout',
    lastModified: now,
  };

  return { cabinet };
}

module.exports = { buildLowerCabinetForSegment };
